import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useCompany } from '../context/CompanyContext';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v/1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v/1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

function Section({ title, color, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: color + '10' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color }}>{title}</h3>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

function LineRow({ label, value, bold, indent, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: `${bold ? 10 : 7}px ${indent ? 16 : 0}px`,
      borderTop: bold ? '1px solid #e5e7eb' : undefined,
      borderBottom: bold ? '2px solid #e5e7eb' : undefined,
      background: highlight ? '#f0fdf4' : undefined,
      borderRadius: highlight ? 6 : undefined,
    }}>
      <span style={{ fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: bold ? '#111827' : '#374151' }}>
        {indent && <span style={{ marginRight: 8, color: '#9ca3af' }}>—</span>}{label}
      </span>
      <span style={{ fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 500, color: highlight ? '#059669' : bold ? '#111827' : '#374151', fontFamily: 'monospace' }}>
        ₹ {fmt(value)}
      </span>
    </div>
  );
}

export default function BalanceSheet() {
  const { currency } = useCompany();
  const [year, setYear] = useState(currentYear);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [tab, setTab] = useState('summary'); // summary | monthly | details

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data: res } = await api.get('/accounting/balance-sheet', { params: { year } });
      setData(res.data);
    } catch { setError('Failed to load balance sheet data.'); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const profitColor = (v) => Number(v) >= 0 ? '#059669' : '#dc2626';

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Balance Sheet & P&L</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>Assets, liabilities and profit & loss report</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Financial Year:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, background: '#fff' }}>
            {YEARS.map(y => <option key={y} value={y}>FY {y}-{String(y+1).slice(-2)}</option>)}
          </select>
          <button onClick={load} style={{ padding: '7px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 80 }}><div className="spinner spinner-lg" /></div>}
      {error   && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          {/* Summary KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
            {[
              { label: 'Total Revenue',     value: data.pnl.revenue,      color: '#2563eb' },
              { label: 'Cash Collected',    value: data.pnl.collected,    color: '#059669' },
              { label: 'Outstanding AR',    value: data.pnl.outstanding,  color: '#d97706' },
              { label: 'Total Expenses',    value: data.pnl.expenses,     color: '#7c3aed' },
              { label: 'Gross Profit',      value: data.pnl.gross_profit, color: data.pnl.gross_profit >= 0 ? '#059669' : '#dc2626' },
              { label: 'Net Profit',        value: data.pnl.net_profit,   color: data.pnl.net_profit >= 0 ? '#059669' : '#dc2626' },
            ].map(c => (
              <div key={c.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderTop: `3px solid ${c.color}` }}>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{c.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: c.color, fontFamily: 'monospace' }}>{fmtShort(c.value)}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
            {[['summary','Summary'], ['monthly','Monthly Chart'], ['pnl','P & L Statement']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding: '9px 18px', border: 'none', borderBottom: tab === k ? '2px solid #2563eb' : '2px solid transparent', background: 'none', fontWeight: tab === k ? 700 : 400, color: tab === k ? '#2563eb' : '#6b7280', cursor: 'pointer', marginBottom: -2, fontSize: 14 }}>
                {l}
              </button>
            ))}
          </div>

          {/* Summary tab */}
          {tab === 'summary' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Assets */}
              <Section title="ASSETS" color="#2563eb">
                <LineRow label="Current Assets" bold />
                <LineRow label="Cash & Bank Balance"    value={data.assets.current.cash_and_bank}       indent />
                <LineRow label="Accounts Receivable"    value={data.assets.current.accounts_receivable} indent />
                <LineRow label="Inventory Value (Cost)" value={data.assets.current.inventory_value}     indent />
                <LineRow label="Total Current Assets"   value={data.assets.total_current} bold highlight />
                <div style={{ marginTop: 12 }}>
                  <LineRow label="Fixed Assets" bold />
                  <div style={{ fontSize: 12, color: '#9ca3af', padding: '6px 16px' }}>Fixed asset register not configured in CRM</div>
                </div>
                <LineRow label="TOTAL ASSETS" value={data.assets.total_current} bold highlight />
              </Section>

              {/* Liabilities + Equity */}
              <div>
                <Section title="LIABILITIES" color="#dc2626">
                  <LineRow label="Current Liabilities" bold />
                  <LineRow label="GST Payable (Collected)" value={data.liabilities.current.gst_payable} indent />
                  <LineRow label="Total Current Liabilities" value={data.liabilities.total_current} bold />
                </Section>
                <Section title="EQUITY / RETAINED EARNINGS" color="#059669">
                  <LineRow label="Revenue (FY {year})"  value={data.pnl.revenue}      indent />
                  <LineRow label="Less: COGS"           value={data.pnl.cogs}         indent />
                  <LineRow label="Gross Profit"         value={data.pnl.gross_profit} bold />
                  <LineRow label="Less: Operating Expenses" value={data.pnl.expenses} indent />
                  <LineRow label="Net Profit / (Loss)"  value={data.pnl.net_profit}   bold highlight />
                </Section>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: '#6b7280' }}>
                  <b>Note:</b> This is a management report derived from CRM data. It does not replace a formal balance sheet prepared under accounting standards. Fixed assets, capital, loans, and inter-company transactions are not reflected.
                </div>
              </div>
            </div>
          )}

          {/* Monthly Chart tab */}
          {tab === 'monthly' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
                Monthly Revenue vs Expenses vs Collections — FY {year}
              </h3>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={data.monthly} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => fmtShort(v)} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => [`₹${fmt(v)}`, name]} />
                  <Legend />
                  <Bar dataKey="revenue"   name="Revenue"    fill="#2563eb" radius={[3,3,0,0]} />
                  <Bar dataKey="collected" name="Collected"  fill="#059669" radius={[3,3,0,0]} />
                  <Bar dataKey="expenses"  name="Expenses"   fill="#f43f5e" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* P&L tab */}
          {tab === 'pnl' && (
            <Section title={`Profit & Loss Statement — FY ${year}`} color="#374151">
              <div style={{ maxWidth: 540 }}>
                <LineRow label="Revenue from Operations (Invoices)" value={data.pnl.revenue} bold />
                <LineRow label="Less: Cost of Goods Sold (COGS)"    value={data.pnl.cogs}    indent />
                <LineRow label="Gross Profit"                        value={data.pnl.gross_profit} bold
                  highlight={data.pnl.gross_profit >= 0} />
                <div style={{ height: 12 }} />
                <LineRow label="Operating Expenses"   value={data.pnl.expenses} bold />
                <div style={{ height: 12 }} />
                <LineRow label="Net Profit / (Loss)"  value={data.pnl.net_profit} bold highlight={data.pnl.net_profit >= 0} />
                <div style={{ height: 20 }} />
                <LineRow label="Cash Collections (Payments Received)" value={data.pnl.collected}   />
                <LineRow label="Outstanding Receivables"              value={data.pnl.outstanding} />
                <LineRow label="Gross Profit Margin"
                  value={data.pnl.revenue > 0 ? (data.pnl.gross_profit / data.pnl.revenue * 100).toFixed(1) + '%' : '0%'}
                  bold />
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
